import {
  validatePaginatedResponseWithSchema,
  validateWithSchema,
} from "@/helpers/common";
import { formatString } from "@/helpers/formatting";
import { baseCallLogTextSchema } from "@/schemas/communication";
import { axiosClient } from "@/services/client";
import endpoints from "@/services/endpoints";

import type { LogEndpointType } from "@/enums/communicationLogs";
import type { IApplication } from "@/models/applications";
import type { IPaginatedResponse } from "@/models/common";
import type { CallLogText } from "@/models/communication";
import type {
  CallLog,
  CallLogData,
  CallNotePayload,
  CallOutPayload,
  CommunicationFilters,
  CommunicationLogBase,
  CommunicationLogComment,
  CommunicationLogResponse,
  CreateTaskPayload,
  SmsLog,
  SmsLogData,
  SmsSentData,
  SmsSentStandAloneData,
  Task,
  TaskLogData,
} from "@/models/communicationLogs";
import type { SmsModalData } from "@/models/sms";
import type { DealTelephone } from "@/models/users";
import type { ActivityHubOptions } from "@/models/workflows";

class CommunicationService {
  async callOut(
    applicationId: IApplication["id"],
    payload: CallOutPayload
  ): Promise<CallLog> {
    const response = await axiosClient.post(
      formatString(endpoints.COMMUNICATION.CALL_OUT, applicationId),
      payload
    );
    return response.data;
  }

  async getCommunicationList(params: Partial<CommunicationFilters> = {}) {
    const response = await axiosClient.get<CommunicationLogResponse>(
      endpoints.COMMUNICATION.LOGS.ALL_INDEXED,
      {
        params,
      }
    );
    return response.data;
  }

  async getCommunicationLog(logId: CommunicationLogBase["id"]) {
    const url = formatString(endpoints.COMMUNICATION.LOGS.SINGLE, logId);
    const response = await axiosClient.get<CommunicationLogBase>(url);
    return response.data;
  }

  async createTask(payload: CreateTaskPayload) {
    const response = await axiosClient.post<{ data: Task }>(
      formatString(endpoints.COMMUNICATION.TASKS.CREATE),
      payload
    );
    return response.data.data;
  }

  async updateTask(taskId: Task["id"], payload: Partial<TaskLogData>) {
    const response = await axiosClient.patch<{ data: Task }>(
      formatString(endpoints.COMMUNICATION.TASKS.UPDATE, taskId),
      payload
    );
    return response.data.data;
  }

  async createCallNote(applicationId: string, payload: CallNotePayload) {
    const response = await axiosClient.post<{ data: CallLog }>(
      formatString(endpoints.COMMUNICATION.CALL_LOGS.CREATE, applicationId),
      payload
    );
    return response.data.data;
  }

  async updateCallNote(logId: CallLogData["id"], payload: CallNotePayload) {
    const response = await axiosClient.put<{ data: CallLogData }>(
      formatString(endpoints.COMMUNICATION.CALL_LOGS.UPDATE, logId),
      payload
    );
    return response.data.data;
  }

  async uploadCallRecording(
    logId: CallLogData["id"],
    file: File,
    additionalConfig: Record<string, unknown> = {}
  ) {
    const formData = new FormData();
    formData.append("file", file);
    const response = await axiosClient.post<{ data: CallLog }>(
      formatString(endpoints.COMMUNICATION.CALL_LOGS.UPLOAD_RECORDING, logId),
      formData,
      {
        headers: {
          "content-type": "multipart/form-data",
        },
        ...additionalConfig,
      }
    );
    return response.data.data;
  }

  async deleteTask(taskId: Task["id"]) {
    const response = await axiosClient.delete(
      formatString(endpoints.COMMUNICATION.TASKS.UPDATE, taskId)
    );
    return response.data.data;
  }

  async createComment(
    type: LogEndpointType,
    logId: CommunicationLogBase["id"],
    comment: string
  ) {
    const endpoint = formatString(
      endpoints.COMMUNICATION.COMMENTS.ALL,
      type,
      logId
    );
    const response = await axiosClient.post<{ data: CommunicationLogComment }>(
      endpoint,
      { comment }
    );
    return response.data.data;
  }

  async updateComment(
    type: LogEndpointType,
    logId: CommunicationLogBase["id"],
    commentId: CommunicationLogComment["id"],
    comment: string
  ) {
    const endpoint = formatString(
      endpoints.COMMUNICATION.COMMENTS.SINGLE,
      type,
      logId,
      commentId
    );
    const response = await axiosClient.put<{ data: CommunicationLogComment }>(
      endpoint,
      { comment }
    );
    return response.data.data;
  }

  async deleteComment(
    type: LogEndpointType,
    logId: CommunicationLogBase["id"],
    commentId: CommunicationLogComment["id"]
  ) {
    const endpoint = formatString(
      endpoints.COMMUNICATION.COMMENTS.SINGLE,
      type,
      logId,
      commentId
    );
    return await axiosClient.delete(endpoint);
  }

  async blockPhoneNumber(payload: {
    applicationId: string;
    phone: Omit<DealTelephone, "is_primary"> & { name: string };
  }) {
    const response = await axiosClient.post(
      formatString(endpoints.COMMUNICATION.PHONES.BLOCK, payload.applicationId),
      payload.phone
    );
    return response.data;
  }

  async unblockPhoneNumber(payload: {
    applicationId: string;
    phone: {
      number: DealTelephone["number"];
    };
  }) {
    const response = await axiosClient.put(
      formatString(
        endpoints.COMMUNICATION.PHONES.UNBLOCK,
        payload.applicationId
      ),
      payload.phone
    );
    return response.data;
  }

  async getActivityHubInfo(applicationId?: IApplication["id"]) {
    const request = applicationId
      ? formatString(
          endpoints.GET_OPTIONS.ACTIVITY_HUB.TABS.DEAL,
          applicationId
        )
      : endpoints.GET_OPTIONS.ACTIVITY_HUB.TABS.ALL;
    const response = await axiosClient.get<{ data: ActivityHubOptions }>(
      request
    );
    return response.data;
  }

  async assignSmsToApplication(application_id: string, sms_id: number) {
    const response = await axiosClient.put<{ data: SmsLogData }>(
      formatString(endpoints.COMMUNICATION.SMS.ASSIGN_TO_APPLICATION, sms_id),
      {
        application_id,
      }
    );
    return response.data;
  }

  async assignCallToApplication(
    application_id: string,
    call_session_id: number
  ) {
    const response = await axiosClient.put<{ data: CallLogData }>(
      formatString(
        endpoints.COMMUNICATION.CALL_LOGS.ASSIGN_TO_APPLICATION,
        call_session_id
      ),
      {
        application_id,
      }
    );
    return response.data;
  }

  async replyToSms(id: number, message: string, from?: string) {
    const response = await axiosClient.post<{ data: SmsLogData }>(
      formatString(endpoints.COMMUNICATION.SMS.REPLY, id),
      { message, from }
    );
    return response.data;
  }

  async getApplicationSmsModalData(application_ids: string[]) {
    const response = await axiosClient.post<SmsModalData>(
      endpoints.COMMUNICATION.SMS.MODAL_DATA,
      {
        application_ids,
      }
    );
    return response.data;
  }

  async sendSms(payload: SmsSentData) {
    const response = await axiosClient.post<{ data: SmsLogData }>(
      endpoints.COMMUNICATION.SMS.SEND,
      payload
    );
    return response.data;
  }

  async sendSmsStandAlone(payload: SmsSentStandAloneData) {
    const response = await axiosClient.post<{ data: SmsLogData }>(
      endpoints.COMMUNICATION.STANDALONE.SMS,
      payload
    );
    return response.data;
  }

  async getSmsConversation(
    id: number,
    params: { sort_by?: string; page?: number }
  ) {
    const response = await axiosClient.get<IPaginatedResponse<SmsLogData>>(
      formatString(endpoints.COMMUNICATION.SMS.CONVERSATION, id),
      {
        params,
      }
    );
    return response.data;
  }

  async getCallLogTranscriptions(sessionId: string) {
    const response = await axiosClient.get<IPaginatedResponse<CallLogText>>(
      formatString(
        endpoints.COMMUNICATION.CALL_LOGS.TEXT.TRANSCRIPTION.ALL,
        sessionId
      )
    );
    return validatePaginatedResponseWithSchema(
      baseCallLogTextSchema,
      response.data
    );
  }

  async addCallLogTranscription(
    sessionId: string,
    payload: { content: string; provider?: string }
  ) {
    const response = await axiosClient.post<{ data: CallLogText }>(
      formatString(
        endpoints.COMMUNICATION.CALL_LOGS.TEXT.TRANSCRIPTION.CREATE,
        sessionId
      ),
      payload
    );
    return validateWithSchema(baseCallLogTextSchema, response.data.data);
  }

  async updateCallLogTranscription(
    sessionId: string,
    transcriptionId: number,
    payload: { content: string; provider?: string | null }
  ) {
    const response = await axiosClient.put<{ data: CallLogText }>(
      formatString(
        endpoints.COMMUNICATION.CALL_LOGS.TEXT.TRANSCRIPTION.SINGLE,
        sessionId,
        transcriptionId
      ),
      payload
    );
    return validateWithSchema(baseCallLogTextSchema, response.data.data);
  }

  async deleteCallLogTranscription(sessionId: string, transcriptionId: number) {
    await axiosClient.delete(
      formatString(
        endpoints.COMMUNICATION.CALL_LOGS.TEXT.TRANSCRIPTION.SINGLE,
        sessionId,
        transcriptionId
      )
    );
  }

  async getCallLogSummaries(sessionId: string) {
    const response = await axiosClient.get<IPaginatedResponse<CallLogText>>(
      formatString(
        endpoints.COMMUNICATION.CALL_LOGS.TEXT.SUMMARY.ALL,
        sessionId
      )
    );
    return validatePaginatedResponseWithSchema(
      baseCallLogTextSchema,
      response.data
    );
  }

  async addCallLogSummary(
    sessionId: string,
    payload: { content: string; provider?: string }
  ) {
    const response = await axiosClient.post<{ data: CallLogText }>(
      formatString(
        endpoints.COMMUNICATION.CALL_LOGS.TEXT.SUMMARY.CREATE,
        sessionId
      ),
      payload
    );
    return validateWithSchema(baseCallLogTextSchema, response.data.data);
  }

  async updateCallLogSummary(
    sessionId: string,
    summaryId: number,
    payload: { content: string; provider?: string | null }
  ) {
    const response = await axiosClient.put<{ data: CallLogText }>(
      formatString(
        endpoints.COMMUNICATION.CALL_LOGS.TEXT.SUMMARY.SINGLE,
        sessionId,
        summaryId
      ),
      payload
    );
    return validateWithSchema(baseCallLogTextSchema, response.data.data);
  }

  async deleteCallLogSummary(sessionId: string, summaryId: number) {
    await axiosClient.delete(
      formatString(
        endpoints.COMMUNICATION.CALL_LOGS.TEXT.SUMMARY.SINGLE,
        sessionId,
        summaryId
      )
    );
  }

  async standaloneCall(
    payload: Pick<CallOutPayload, "to" | "provider">
  ): Promise<CallLog> {
    const response = await axiosClient.post<CallLog>(
      endpoints.COMMUNICATION.STANDALONE.CALLS,
      payload
    );
    return response.data;
  }

  async standaloneSms(): Promise<SmsLog> {
    const response = await axiosClient.post<SmsLog>(
      endpoints.COMMUNICATION.STANDALONE.SMS
    );
    return response.data;
  }
}

const communicationService = new CommunicationService();
export default communicationService;
